import type { CopilotPatchOp } from "@CommonApi/merchflows/_merchflowId_/config-copilot";
import type { PspRules } from "@CommonTypes/backend/PspRules";

type Json = Record<string, unknown>;

const isObject = (value: unknown): value is Json =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const isBand = (path: string) => /(^|\.)item_shelf_bands\./.test(path);

const isUnsafePath = (path: string) =>
  path.split(".").some((key) => !key || ["__proto__", "prototype", "constructor"].includes(key));

// Older band ops carry `to: null` without `toPresent`: removing the band.
const removesKey = ({ path, to, toPresent }: Pick<CopilotPatchOp, "path" | "to" | "toPresent">) =>
  toPresent === false ||
  to === undefined ||
  (toPresent === undefined && to === null && isBand(path));

export const patchedRules = (
  rules: PspRules,
  ops: readonly Pick<CopilotPatchOp, "path" | "to" | "toPresent" | "toAbsentParents">[],
): PspRules => {
  const copy = structuredClone(rules) as unknown as Json;
  for (const op of ops) {
    const { path, to, toAbsentParents } = op;
    if (isUnsafePath(path)) throw new Error("Invalid rule path");
    const keys = path.split(".");
    let node = copy;
    for (const key of keys.slice(0, -1)) {
      const next = node[key];
      if (isObject(next)) {
        node = next;
      } else {
        const created: Json = {};
        node[key] = created;
        node = created;
      }
    }
    const last = keys[keys.length - 1];
    if (removesKey(op)) delete node[last];
    else node[last] = to;
    for (const parent of [...(toAbsentParents ?? [])].reverse()) {
      if (!path.startsWith(`${parent}.`) || isUnsafePath(parent)) continue;
      const parentKeys = parent.split(".");
      let owner = copy;
      for (const key of parentKeys.slice(0, -1)) {
        const next = owner[key];
        if (!isObject(next)) break;
        owner = next;
      }
      const key = parentKeys[parentKeys.length - 1];
      const candidate = owner[key];
      if (isObject(candidate) && Object.keys(candidate).length === 0) delete owner[key];
    }
  }
  return copy as unknown as PspRules;
};

export const undoOps = (ops: readonly CopilotPatchOp[]) =>
  ops.map(({ path, from, fromPresent, fromAbsentParents }) => ({
    path,
    to: from,
    ...(fromPresent !== undefined || from == null ? { toPresent: fromPresent ?? false } : {}),
    ...(fromAbsentParents ? { toAbsentParents: fromAbsentParents } : {}),
  }));

export const presentAt = (rules: PspRules, path: string) => {
  let node: unknown = rules;
  for (const key of path.split(".")) {
    if (!isObject(node) || !Object.hasOwn(node, key)) return false;
    node = node[key];
  }
  return true;
};
export const absentParents = (rules: PspRules, path: string) =>
  path
    .split(".")
    .slice(0, -1)
    .map((_key, index, keys) => keys.slice(0, index + 1).join("."))
    .filter((parent) => !presentAt(rules, parent));

export const valueAt = (rules: PspRules | null | undefined, path: string): unknown =>
  path.split(".").reduce<unknown>((node, key) => (isObject(node) ? node[key] : undefined), rules);

// The rules modal rebuilds objects such as the divider rule in its own key order.
const sortedKeys = (value: unknown): unknown =>
  Array.isArray(value)
    ? value.map(sortedKeys)
    : isObject(value)
      ? Object.fromEntries(
          Object.keys(value)
            .sort()
            .map((key) => [key, sortedKeys(value[key])]),
        )
      : value;

export const isSameValue = (a: unknown, b: unknown) =>
  a === b ||
  (a !== undefined &&
    b !== undefined &&
    JSON.stringify(sortedKeys(a)) === JSON.stringify(sortedKeys(b)));

export const changedPaths = (before: unknown, after: unknown, at = ""): string[] => {
  if (isObject(before) && isObject(after)) {
    const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
    return [...keys].flatMap((key) =>
      changedPaths(before[key], after[key], at ? `${at}.${key}` : key),
    );
  }
  return isSameValue(before, after) ? [] : [at];
};

export const holds = (
  rules: PspRules,
  ops: readonly Pick<CopilotPatchOp, "path" | "to" | "toPresent">[],
): boolean =>
  ops.length > 0 &&
  ops.every((op) =>
    removesKey(op)
      ? !presentAt(rules, op.path)
      : (op.toPresent === undefined || presentAt(rules, op.path)) &&
        isSameValue(valueAt(rules, op.path), op.to),
  );
